import React, { useLayoutEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { LocationProvider } from "../contexts/LocationContext";
import { useAuth } from "../contexts/AuthContext";
import Spinner from "../ui/Spinner";
import { devLog } from "../utils/logger";
import { setRedirectAfterAuth } from "../utils/redirectAfterAuth";

export function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  /**
   * Setting up global geolocation storage before any location-dependent components mount
   * Using useLayoutEffect instead of useEffect because it runs synchronously (before DOM is painted) instead of ansynchronously (after DOM is painted)
   *  React executes useEffects in this order: child component's useEffects first, then parent's
   *  If we used useEffect instead, the useEffect in LocationContext requesting a location would run before this one
   *    We couldn't have that because we use this global original geolocation to request data
   */
  useLayoutEffect(() => {
    devLog("ProtectedRoute: Setting up global geolocation storage");

    /**
     * Store the original geolocation API globally before any component replaces it
     * This ensures LocationContext can always access real GPS data
     * even after MapboxMap replaces navigator.geolocation with mock implementation
     */
    if (!window.originalGeolocation && window.navigator.geolocation) {
      window.originalGeolocation = window.navigator.geolocation;
      devLog("ProtectedRoute: Original geolocation stored globally");
    }

    /**
     * Cleanup function to restore original geolocation when leaving protected routes
     */
    return () => {
      devLog("ProtectedRoute: Cleaning up geolocation setup");
      /**
       * Restore original geolocation if it was replaced
       */
      if (window.originalGeolocation) {
        Object.defineProperty(window.navigator, "geolocation", {
          value: window.originalGeolocation,
          writable: false,
          configurable: true,
        });
      }
      /**
       * Clear the global reference
       */
      delete window.originalGeolocation;
    };
  }, []);

  // Show loading spinner while authentication state is initializing
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner size={40} className="text-primary" />
      </div>
    );
  }

  // Redirect to login if not authenticated, preserving the attempted URL.
  if (!isAuthenticated) {
    const intended = location.pathname + location.search;
    // Persist the intended destination in sessionStorage so it survives the
    // async auth-init render cycles and a possible apex->www full reload. The
    // util self-validates (only safe internal paths are stored). We also keep
    // state.from as a harmless in-memory fallback. Writing inline during render
    // is intentional: it's idempotent and this component unmounts immediately
    // via <Navigate replace />.
    setRedirectAfterAuth(intended);
    return <Navigate to="/login" state={{ from: intended }} replace />;
  }

  // User is authenticated - render children with LocationProvider
  return <LocationProvider>{children}</LocationProvider>;
}
